import { z } from "zod";

/**
 * Shared DTO cho device-token register/remove (US-E12.F1-0067-S02 · Push Notification Delivery).
 *
 * Một nguồn sự thật shape api ↔ mobile (DRY): app gửi token OS + platform lên backend để
 * outbox-dispatcher push tới thiết bị. Delivery layer cho E9 notif (0057–0062).
 *
 * BẢO MẬT (invariants):
 *  - C5 validate biên: `.strict()` chặn field lạ (KHÔNG nhận userId từ body — self-only, userId lấy
 *    từ token). platform enum ios|android (KHÔNG sms — guardrail C1).
 *  - PII: token là định danh thiết bị — KHÔNG log raw (redact ở logger/adapter layer).
 *  - token có giới hạn độ dài để chặn input phình to tại biên.
 */

/** Độ dài tối đa của device token (FCM/APNs token ~ 152–200 ký tự; nới rộng an toàn). */
export const DEVICE_TOKEN_MAX_LENGTH = 4096;

/** Platform hợp lệ — chỉ ios|android (KHÔNG sms — guardrail C1). */
export const devicePlatformSchema = z.enum(["ios", "android"]);
export type DevicePlatform = z.infer<typeof devicePlatformSchema>;

/**
 * Body POST /v1/me/device-tokens — { token, platform }.
 * `.strict()` chặn userId/field lạ (self-only: token gắn userId của caller từ token, KHÔNG từ body).
 */
export const registerDeviceTokenRequestSchema = z
  .object({
    token: z.string().min(1).max(DEVICE_TOKEN_MAX_LENGTH),
    platform: devicePlatformSchema,
  })
  .strict();
export type RegisterDeviceTokenRequest = z.infer<typeof registerDeviceTokenRequestSchema>;

/** Response register — trạng thái upsert (KHÔNG lộ raw token / userId). */
export const registerDeviceTokenResponseSchema = z
  .object({
    registered: z.literal(true),
    platform: devicePlatformSchema,
  })
  .strict();
export type RegisterDeviceTokenResponse = z.infer<typeof registerDeviceTokenResponseSchema>;

/**
 * Body DELETE /v1/me/device-tokens — { token }.
 * Xoá token của CHÍNH caller (logout/đổi máy). `.strict()` chặn userId/field lạ.
 */
export const removeDeviceTokenRequestSchema = z
  .object({
    token: z.string().min(1).max(DEVICE_TOKEN_MAX_LENGTH),
  })
  .strict();
export type RemoveDeviceTokenRequest = z.infer<typeof removeDeviceTokenRequestSchema>;

/** Response remove — số row đã xoá (0 nếu token không thuộc caller — idempotent). */
export const removeDeviceTokenResponseSchema = z
  .object({
    removed: z.boolean(),
  })
  .strict();
export type RemoveDeviceTokenResponse = z.infer<typeof removeDeviceTokenResponseSchema>;
